import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ContentStyle from "styles/application/ContentStyle";
import DetailsPanel from "components/Elements/DetailsPanel";
import TabTitle from "components/Elements/TabTitle";
import {
	getAvailableLocales,
	getLocaleSettings,
	updateLocaleSettings,
} from "services/localisation";
import { setAppLocale } from "i18n";
import { showError, showSuccess } from "../../../../../redux/common/actions";

const AC = ContentStyle();

const localeLabel = (tag) => {
	try {
		const name = new Intl.DisplayNames([tag], { type: "language" }).of(tag);
		return name && name !== tag ? `${name} (${tag})` : tag;
	} catch {
		return tag;
	}
};

const BASELINE = "en";

const LanguageSettingsContent = ({ state }) => {
	const reduxDispatch = useDispatch();
	const { t } = useTranslation();
	const { appId } = useParams();
	// The admin config context carries no SiteAppID claim, so target the site app
	// by id from the URL.
	const scope = useMemo(
		() => ({ level: "siteapp", id: Number(appId) }),
		[appId]
	);
	const readOnly = !!state?.isReadOnly;
	const appName = state?.details?.data?.application?.name ?? "";

	const [available, setAvailable] = useState([]);
	const [defaultLocale, setDefaultLocale] = useState(BASELINE);
	const [enabled, setEnabled] = useState([BASELINE]);
	const [savedDefault, setSavedDefault] = useState(BASELINE);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		let active = true;

		(async () => {
			const [localesRes, settingsRes] = await Promise.all([
				getAvailableLocales(),
				getLocaleSettings(scope),
			]);

			if (!active) return;

			if (localesRes?.status) setAvailable(localesRes.data ?? [BASELINE]);
			if (settingsRes?.status) {
				const d = settingsRes.data.defaultLocale ?? BASELINE;
				setDefaultLocale(d);
				setSavedDefault(d);
				setEnabled(settingsRes.data.enabledLocales ?? [BASELINE]);
			} else {
				reduxDispatch(showError(t("message.failedToLoadLanguageSettings")));
			}
			setLoading(false);
		})();

		return () => {
			active = false;
		};
	}, [reduxDispatch, scope, t]);

	const toggleEnabled = (tag) => {
		if (tag === BASELINE) return; // English is the locked fallback baseline
		setEnabled((current) => {
			const next = current.includes(tag)
				? current.filter((t) => t !== tag)
				: [...current, tag];
			// Never leave the default disabled.
			if (!next.includes(defaultLocale)) {
				setDefaultLocale(BASELINE);
			}
			return next;
		});
	};

	const handleSave = useCallback(async () => {
		setSaving(true);
		const enabledList = [...new Set([...enabled, BASELINE])];
		const response = await updateLocaleSettings({
			defaultLocale,
			enabledLocales: enabledList,
			scope,
		});

		if (!response?.status) {
			setSaving(false);
			reduxDispatch(
				showError(
					typeof response?.data === "string"
						? response.data
						: t("message.failedToSaveLanguageSettings")
				)
			);
			return;
		}

		reduxDispatch(showSuccess(t("message.languageSettingsSaved")));

		// If the default changed, switch the whole app to it (reloads).
		if (defaultLocale !== savedDefault) {
			await setAppLocale(defaultLocale);
			return;
		}

		setSavedDefault(defaultLocale);
		setSaving(false);
	}, [defaultLocale, enabled, savedDefault, reduxDispatch, scope, t]);

	if (loading) {
		return (
			<AC.SpinnerContainer>
				<CircularProgress />
			</AC.SpinnerContainer>
		);
	}

	const enabledOptions = [...new Set([BASELINE, ...enabled])];

	return (
		<div>
			<TabTitle
				title={t("title.appLanguageSettings", { appName })}
			/>

			<div className="detailsContainer">
				<DetailsPanel
					header={t("title.languageSettings")}
					dataCount={null}
					description={t("message.languageSettingsDescription")}
				/>
			</div>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 520 }}>
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						{t("label.defaultLanguage")}
					</Typography>
					<TextField
						select
						size="small"
						fullWidth
						value={defaultLocale}
						disabled={readOnly || saving}
						onChange={(e) => setDefaultLocale(e.target.value)}
					>
						{enabledOptions.map((tag) => (
							<MenuItem key={tag} value={tag}>
								{localeLabel(tag)}
							</MenuItem>
						))}
					</TextField>
				</Box>

				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						{t("label.availableLanguages")}
					</Typography>
					{available.map((tag) => (
						<FormControlLabel
							key={tag}
							control={
								<Checkbox
									checked={tag === BASELINE || enabled.includes(tag)}
									disabled={readOnly || saving || tag === BASELINE}
									onChange={() => toggleEnabled(tag)}
								/>
							}
							label={
								tag === BASELINE
									? t("label.localeAlwaysOn", {
											locale: localeLabel(tag),
									  })
									: localeLabel(tag)
							}
						/>
					))}
				</Box>

				<Box>
					<Button
						variant="contained"
						disabled={readOnly || saving}
						onClick={handleSave}
					>
						{saving ? t("message.savingEllipsis") : t("action.save")}
					</Button>
				</Box>
			</Box>
		</div>
	);
};

export default LanguageSettingsContent;
